import React, { useEffect, useMemo, useState } from 'react';
import {
  Button,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  AlertDialogCloseButton,
  Text,
  Box,
} from '@chakra-ui/react';
import { useForm } from 'react-hook-form';
import InputController from 'components/Form/InputController';
import { ModalType } from 'constants/common';
import { toast } from 'components/Toast';
import { useUpdateCategoryMutation, useCreateCategoryMutation } from 'services/category';
import { ExportFormValidate } from 'utils/validation';
import { yupResolver } from '@hookform/resolvers/yup';
import SelectController from 'components/Form/SelectController';

const optionPageSize = [
  {
    label: 10,
    value: 10,
  },
  {
    label: 50,
    value: 50,
  },
  {
    label: 100,
    value: 100,
  },
  {
    label: 500,
    value: 500,
  },
  {
    label: 1000,
    value: 1000,
  },
  {
    label: 2000,
    value: 2000,
  },
];

const ExportModal = ({ isOpen, handleExport, onClose, totalRecords }) => {
  const cancelRef = React.useRef();
  const createCategoryMutation = useCreateCategoryMutation();
  const updateCategoryMutation = useUpdateCategoryMutation();
  const { control, handleSubmit, getValues, watch } = useForm({
    resolver: yupResolver(ExportFormValidate),
  });
  const pageSize = Number(watch().number?.value);

  const onSubmit = values => {
    handleExport(Number(values?.number?.value), currentPage - 1);
  };

  const [currentPage, setCurrentPage] = useState(1);

  const paginationParams = useMemo(() => {
    const totalPages = Math.ceil(totalRecords / pageSize);
    const firstPageIndex = (currentPage - 1) * pageSize;
    const lastPageIndex = Math.min(firstPageIndex + pageSize - 1, totalRecords - 1);

    let pageNumbers = [];
    if (totalPages <= 5) {
      pageNumbers = Array.from({ length: totalPages }, (_, i) => i + 1);
    } else {
      if (currentPage <= 3) {
        pageNumbers = [1, 2, 3, 4, 5, '...', totalPages];
      } else if (currentPage >= totalPages - 2) {
        pageNumbers = [1, '...', totalPages - 4, totalPages - 3, totalPages - 2, totalPages - 1, totalPages];
      } else {
        pageNumbers = [1, '...', currentPage - 1, currentPage, currentPage + 1, '...', totalPages];
      }
    }

    return {
      totalPages,
      firstPageIndex,
      lastPageIndex,
      pageNumbers,
    };
  }, [currentPage, totalRecords, pageSize]);

  const handlePageChange = page => {
    if (page >= 1 && page <= paginationParams.totalPages) {
      setCurrentPage(page);
    }
  };
  return (
    <>
      <AlertDialog
        motionPreset="slideInBottom"
        leastDestructiveRef={cancelRef}
        onClose={() => {
          onClose(ModalType.Adjust);
        }}
        isOpen={isOpen}
        isCentered
      >
        <AlertDialogOverlay />
        <AlertDialogContent maxW={'600px'} maxH={'400px'}>
          <AlertDialogHeader textTransform="uppercase">Nhập số lượng record tải xuống</AlertDialogHeader>
          <AlertDialogCloseButton />
          <AlertDialogBody>
            {/* <InputController control={control} name="number" type="number" label="Số lượng" /> */}
            <SelectController
              label="Số lượng"
              name="number"
              placeholder="Chọn số lượng"
              control={control}
              options={optionPageSize}
              isRequired
              styleContainer={{ pt: '4' }}
              onChange={() => setCurrentPage(1)}
            />
            <div>
              {!!pageSize && (
                <>
                  <div>
                    Các bản ghi từ {paginationParams.firstPageIndex + 1} đến {paginationParams.lastPageIndex + 1} trong {totalRecords} bản
                    ghi
                  </div>
                  <Text mt={5}>Chọn trang</Text>
                  <Box mt={2} display={'flex'} gap={2}>
                    <Button onClick={() => handlePageChange(currentPage - 1)} isDisabled={currentPage === 1}>
                      Trước
                    </Button>

                    {paginationParams.pageNumbers.map((page, index) => (
                      <Button
                        key={index}
                        onClick={() => typeof page === 'number' && handlePageChange(page)}
                        background={currentPage === page ? 'blue.500' : 'gray.100'}
                        className={`px-3 py-1 rounded border ${currentPage === page ? 'bg-blue-500 text-white' : 'hover:bg-gray-100'} ${
                          typeof page !== 'number' ? 'cursor-default' : ''
                        }`}
                      >
                        {page}
                      </Button>
                    ))}

                    <Button onClick={() => handlePageChange(currentPage + 1)} isDisabled={currentPage === paginationParams.totalPages}>
                      Sau
                    </Button>
                  </Box>
                </>
              )}
            </div>
          </AlertDialogBody>
          <AlertDialogFooter>
            <Button
              ref={cancelRef}
              onClick={() => {
                onClose(ModalType.Adjust);
              }}
            >
              Hủy
            </Button>
            <Button
              colorScheme="blue"
              ml={3}
              isLoading={createCategoryMutation.isPending || updateCategoryMutation.isPending}
              onClick={handleSubmit(onSubmit)}
            >
              Tải xuống
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default ExportModal;
